import React from "react";
import {
  Box,
  Container,
  Grid,
  GridItem,
  Heading,
  Text,
  VStack,
  useColorModeValue,
  Button,
  Flex,
} from "@chakra-ui/react";
import { Plane, Calendar, Users, Building2, ArrowRight } from "lucide-react";
import { useText, useLocalPath } from "../../i18n/lang";

const TEXT = {
  en: {
    title: "Our Services",
    intro: "Comprehensive travel solutions designed to meet every business need, from corporate travel management to specialized B2B platforms.",
    services: [
      {
        title: "Corporate Travel Management",
        description: "Comprehensive travel solutions with cutting-edge technology, cost optimization, and 24/7 support for seamless corporate journeys.",
        highlights: ["Smart booking platforms", "Cost optimization", "24/7 emergency support"],
      },
      {
        title: "Events Management",
        description: "End-to-end event planning from intimate meetings to large conferences with dedicated project management and full budget control.",
        highlights: ["Venue selection", "Budget transparency", "Full event logistics"],
      },
      {
        title: "B2B Consolidator",
        description: "BOOKING TIME platform for travel agencies with access to 800,000+ hotels, major airlines, and exclusive negotiated fares.",
        highlights: ["BOOKING TIME platform", "Global suppliers", "Special negotiated fares"],
      },
      {
        title: "Consultancy",
        description: "Expert travel consulting services to optimize your travel strategy, improve compliance, and enhance traveler satisfaction.",
        highlights: ["Travel strategy", "Compliance optimization", "Expert guidance"],
      },
    ],
    learnMore: "Learn More",
    cta: "Ready to transform your travel experience? Let's discuss how our services can be tailored to your specific business needs.",
    ctaButton: "Get Started Today",
  },
  ro: {
    title: "Serviciile noastre",
    intro: "Soluții complete de călătorie pentru fiecare nevoie a companiei tale, de la managementul călătoriilor corporate până la platforme B2B specializate.",
    services: [
      {
        title: "Managementul călătoriilor de afaceri",
        description: "Soluții complete de călătorie cu tehnologie de ultimă generație, optimizarea costurilor și suport 24/7 pentru călătorii corporate fără griji.",
        highlights: ["Platforme inteligente de rezervare", "Optimizarea costurilor", "Suport 24/7 pentru urgențe"],
      },
      {
        title: "Managementul evenimentelor",
        description: "Planificare completă, de la întâlniri restrânse la conferințe ample, cu manager de proiect dedicat și control total al bugetului.",
        highlights: ["Selecția locației", "Transparența bugetului", "Logistică completă"],
      },
      {
        title: "Consolidator B2B",
        description: "Platforma BOOKING TIME pentru agenții de turism, cu acces la peste 800.000 de hoteluri, marile companii aeriene și tarife negociate exclusive.",
        highlights: ["Platforma BOOKING TIME", "Furnizori globali", "Tarife negociate speciale"],
      },
      {
        title: "Consultanță",
        description: "Servicii de consultanță pentru optimizarea strategiei de călătorii, creșterea conformității și satisfacția călătorilor.",
        highlights: ["Strategie de călătorii", "Optimizarea conformității", "Îndrumare de specialitate"],
      },
    ],
    learnMore: "Află mai multe",
    cta: "Vrei să transformi experiența de călătorie a companiei tale? Hai să discutăm cum putem adapta serviciile noastre nevoilor tale de business.",
    ctaButton: "Începe acum",
  },
};

const ServicesSummary = () => {
  const t = useText(TEXT);
  const localPath = useLocalPath();
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  // Navigation function
  const handleNavigation = (path) => {
    window.location.href = path;
  };

  // Secțiunea fiecărui serviciu de pe pagina Servicii (aceeași ordine ca t.services)
  const SERVICE_ANCHORS = [
    "#corporate-travel",
    "#events-management",
    "#b2b-consolidator",
    "#consultancy",
  ];

  const services = [
    {
      title: t.services[0].title,
      icon: Plane,
      description: t.services[0].description,
      highlights: t.services[0].highlights,
      color: "blue.500",
      gradient: "linear(135deg, blue.400, blue.600)",
    },
    {
      title: t.services[1].title,
      icon: Calendar,
      description: t.services[1].description,
      highlights: t.services[1].highlights,
      color: "purple.500",
      gradient: "linear(135deg, purple.400, purple.600)",
    },
    {
      title: t.services[2].title,
      icon: Building2,
      description: t.services[2].description,
      highlights: t.services[2].highlights,
      color: "orange.500",
      gradient: "linear(135deg, orange.400, orange.600)",
    },
    {
      title: t.services[3].title,
      icon: Users,
      description: t.services[3].description,
      highlights: t.services[3].highlights,
      color: "green.500",
      gradient: "linear(135deg, green.400, green.600)",
    },
  ];

  return (
    <Box py={{ base: 16, md: 24 }} bg={bgColor}>
      <Container maxW="container.xl">
        <VStack spacing={12} align="center">
          {/* Header Section */}
          <VStack spacing={4} textAlign="center" maxW="3xl">
            <Heading
              as="h2"
              size="2xl"
              color="gray.800"
              fontFamily="Montserrat"
              fontWeight="700"
            >
              {t.title}
            </Heading>
            <Text
              fontSize="xl"
              color="gray.600"
              lineHeight="tall"
              fontFamily="Montserrat"
            >
              {t.intro}
            </Text>
          </VStack>

          {/* Services Grid */}
          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              xl: "repeat(4, 1fr)",
            }}
            gap={{ base: 6, md: 8 }}
            w="full"
          >
            {services.map((service, idx) => (
              <GridItem key={idx}>
                <Box
                  bg={cardBg}
                  p={6}
                  borderRadius="2xl"
                  borderWidth="1px"
                  borderColor={borderColor}
                  boxShadow="lg"
                  transition="all 0.3s ease"
                  _hover={{
                    transform: "translateY(-8px)",
                    boxShadow: "2xl",
                    borderColor: service.color,
                  }}
                  position="relative"
                  overflow="hidden"
                  h="full"
                >
                  {/* Gradient Accent */}
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    h="4px"
                    bgGradient={service.gradient}
                  />

                  <VStack spacing={4} align="start" h="full">
                    {/* Icon and Title */}
                    <Flex align="center" gap={3}>
                      <Box
                        p={3}
                        borderRadius="xl"
                        bg={`${service.color.split(".")[0]}.50`}
                        color={service.color}
                      >
                        <service.icon size={24} strokeWidth={2} />
                      </Box>
                      <Heading
                        as="h3"
                        size="md"
                        color="gray.800"
                        fontFamily="Montserrat"
                        fontWeight="600"
                        lineHeight="short"
                      >
                        {service.title}
                      </Heading>
                    </Flex>

                    {/* Description */}
                    <Text
                      color="gray.600"
                      fontSize="sm"
                      lineHeight="tall"
                      fontFamily="Montserrat"
                      flex="1"
                    >
                      {service.description}
                    </Text>

                    {/* Highlights */}
                    <VStack spacing={2} align="start" w="full">
                      {service.highlights.map((highlight, highlightIdx) => (
                        <Flex key={highlightIdx} align="center" gap={2}>
                          <Box
                            w="2"
                            h="2"
                            borderRadius="full"
                            bg={service.color}
                          />
                          <Text
                            fontSize="xs"
                            color="gray.700"
                            fontFamily="Montserrat"
                            fontWeight="500"
                          >
                            {highlight}
                          </Text>
                        </Flex>
                      ))}
                    </VStack>

                    {/* Learn More Button */}
                    <Button
                      size="sm"
                      variant="ghost"
                      color={service.color}
                      fontFamily="Montserrat"
                      fontWeight="600"
                      rightIcon={<ArrowRight size={16} />}
                      onClick={() =>
                        handleNavigation(localPath("/servicii") + SERVICE_ANCHORS[idx])
                      }
                      _hover={{
                        bg: `${service.color.split(".")[0]}.50`,
                        transform: "translateX(4px)",
                      }}
                      transition="all 0.2s ease"
                      alignSelf="start"
                    >
                      {t.learnMore}
                    </Button>
                  </VStack>
                </Box>
              </GridItem>
            ))}
          </Grid>

          {/* CTA Section */}
          <VStack spacing={6} textAlign="center">
            <Text
              fontSize="lg"
              color="gray.600"
              fontFamily="Montserrat"
              maxW="2xl"
            >
              {t.cta}
            </Text>
            <Button
              as="a"
              href={localPath("/contact")}
              cursor="pointer"
              size="lg"
              colorScheme="blue"
              fontFamily="Montserrat"
              fontWeight="600"
              color="blue.900"
              px={8}
              py={6}
              fontSize="lg"
              rightIcon={<ArrowRight size={20} />}
              onClick={() => handleNavigation(localPath("/contact"))}
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "xl",
                color: "white",
              }}
              transition="all 0.3s ease"
            >
              {t.ctaButton}
            </Button>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
};

export default ServicesSummary;
